/**
 * Real `SkillAPI` implementation backed by the HTTP client (ADR-0019 §3.2).
 *
 * Wire mapping:
 *   - listDefaults: NO server route — returns `DEFAULT_SKILLS` from
 *                   `@sill/types` (same source the server seeds from at
 *                   site creation, `services/api/src/http/routes/v1/
 *                   sites.ts` seed-skills-defaults). No HTTP call.
 *   - listForSite:  GET /v1/skills?site_id=...  200 + `{ items }`
 *   - saveForSite:  diff against current state, emit one
 *                   `POST /v1/skills/toggle` per changed skill, serial,
 *                   in skill_id order. See §3.3 for the design rationale.
 *
 * The `saveForSite` adapter is the most subtle method here. The mock
 * accepts a full skill list and overwrites in-memory state in one call;
 * the server is one-toggle-per-call. The adapter:
 *
 *   1. Fetches the current skill list for the site (one extra GET).
 *   2. Diffs `next` against current to find which skills' `enabled`
 *      changed.
 *   3. Emits one `POST /v1/skills/toggle` per change, in skill_id order,
 *      **serially** (not Promise.all).
 *
 * Serial-not-parallel is load-bearing: a 401 mid-sequence (session
 * expired after toggle 2 of 5) halts the loop because the HttpClient
 * throws `AuthUnauthorizedError` on 401 and the `await` short-circuits.
 * A parallel `Promise.all` would issue all toggles before the 401
 * reached the caller; successes before the auth check would commit to
 * the DB. Serial preserves "no further server writes after the auth
 * check fails" semantics (§3.3).
 *
 * Order by skill_id is deterministic — a failed N-th toggle leaves a
 * predictable partial-state the dashboard can report. Match the
 * server-side `ORDER BY skill_id` sort for symmetric debugging.
 */

import { DEFAULT_SKILLS } from '@sill/types';
import type { Skill, SiteId, SkillId, SkillWithStatus } from '@sill/types';
import type {
  CreateCustomSkillInput,
  CreateCustomSkillResult,
  PatchWebhookEndpointResult,
  ReprobeSkillResult,
  RotateWebhookSecretResult,
  SkillAPI,
} from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

export interface RealSkillsDeps {
  readonly http: HttpClient;
}

// ADR-0129 §2.4 — the route response is `{ items: SkillWithStatus[] }`
// (additive on top of pre-0129's `Skill[]`; existing readers ignore
// `status` + `gating_reason`). The dashboard now consumes them in
// Settings + Visibility to surface live / not-live / coming-soon /
// tier-gated honestly. The status + gating_reason fields originate at
// origin from the same `CardBackingSignals` the signed agent card uses
// — single source of truth (ADR-0129 §2.1).
interface SkillsListResponse {
  items: SkillWithStatus[];
}

interface SkillToggleResponse {
  skill: Skill;
}

export function createRealSkillsApi(deps: RealSkillsDeps): SkillAPI {
  return {
    async listDefaults(): Promise<Skill[]> {
      // ADR-0019 §3.2 — no server route. The server seeds skills from
      // the same DEFAULT_SKILLS constant at site creation.
      return [...DEFAULT_SKILLS];
    },

    async listForSite(site_id: SiteId): Promise<SkillWithStatus[]> {
      const res = await deps.http.request<SkillsListResponse>({
        method: 'GET',
        path: `/v1/skills?site_id=${encodeURIComponent(site_id)}`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res.items;
    },

    async saveForSite(site_id: SiteId, next: Skill[]): Promise<void> {
      // Fetch current state to compute the diff. One extra GET per
      // save — acceptable cost; passing the current state into the
      // method would be a `packages/types` breakage.
      const cur = await this.listForSite(site_id);
      const cur_by_id = new Map(cur.map((s) => [s.skill_id, s.enabled]));

      const changes: ReadonlyArray<{ skill_id: SkillId; enabled: boolean }> =
        next.flatMap((s) =>
          cur_by_id.get(s.skill_id) === s.enabled
            ? []
            : [{ skill_id: s.skill_id, enabled: s.enabled }],
        );

      const ordered = [...changes].sort((a, b) =>
        a.skill_id < b.skill_id ? -1 : a.skill_id > b.skill_id ? 1 : 0,
      );

      // Serial loop — a 401 mid-sequence halts via the await short-
      // circuit. Phase 1 saves ~7 skills; sub-second latency is fine.
      for (const change of ordered) {
        await deps.http.request<SkillToggleResponse>({
          method: 'POST',
          path: '/v1/skills/toggle',
          body: { site_id, skill_id: change.skill_id, enabled: change.enabled },
        });
      }
    },

    // ADR-0162 §3 Slice 4 — create a merchant-defined custom skill.
    // The `webhook_secret` on the response is shown-once (the origin
    // never re-emits it); the caller MUST surface it on this single
    // response. The origin returns 201 for both discovery + webhook
    // creates; ApiError.server_reason carries the specific 400/409
    // cause for the modal to render inline.
    async createCustom(
      site_id: SiteId,
      input: CreateCustomSkillInput,
    ): Promise<CreateCustomSkillResult> {
      const res = await deps.http.request<CreateCustomSkillResult>({
        method: 'POST',
        path: '/v1/skills',
        body: { site_id, ...input },
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    // ADR-0162 §3a — rotate the per-skill webhook shared secret. The
    // plaintext returned here is the ONLY time it is retrievable; the
    // modal surfaces it in the same shown-once panel used at create.
    async rotateWebhookSecret(
      site_id: SiteId,
      skill_id: string,
    ): Promise<RotateWebhookSecretResult> {
      const res = await deps.http.request<RotateWebhookSecretResult>({
        method: 'POST',
        path: `/v1/skills/${encodeURIComponent(skill_id)}/rotate-secret`,
        body: { site_id },
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    // ADR-0162 §3a — change the webhook fulfillment endpoint. The
    // origin re-probes before returning; the returned skill carries
    // the fresh `endpoint_status`.
    async patchWebhookEndpoint(
      site_id: SiteId,
      skill_id: string,
      fulfillment_endpoint: string,
    ): Promise<PatchWebhookEndpointResult> {
      const res = await deps.http.request<PatchWebhookEndpointResult>({
        method: 'PATCH',
        path: `/v1/skills/${encodeURIComponent(skill_id)}`,
        body: { site_id, fulfillment_endpoint },
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    // ADR-0171 Slice R.1 — re-run the first-activation battery for a
    // Track-B DEFAULT skill row. Same rate limit + auth as create /
    // patch / rotate-secret. The returned `activation_probe_status` is
    // the fresh outcome; the merchant reads the fresh row shape via
    // this response OR the next `listForSite` call. Copy discipline
    // (ADR-0171 §Consequences): callers must render "activation checks
    // passed" or "blocked" — never "endpoint verified" / "secure".
    async reprobeActivationBattery(
      site_id: SiteId,
      skill_id: string,
    ): Promise<ReprobeSkillResult> {
      const res = await deps.http.request<ReprobeSkillResult>({
        method: 'POST',
        path: `/v1/skills/${encodeURIComponent(skill_id)}/reprobe`,
        body: { site_id },
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },
  };
}
